import { call, put, take, fork, all } from "redux-saga/effects";
import { eventChannel, END, buffers } from "redux-saga";
import mediaService from "../services/mediaService";
import { mediaConstants } from "../actions/constants/mediaConstants";
import { commonConstants } from "../actions/constants/commonConstants";
import type { UploadImagesProgress } from "../services/mediaService";

function* uploadMedia(action: any): any {
  try {
    const { files, studioId, eventId, mediaType, serviceId = null } = action.payload;

    if (!files || !files.length) {
      yield put({ type: commonConstants.showErrorMessage, payload: "No files selected for upload" });
      yield put({ 
        type: mediaConstants.uploadMediaFailure, 
        payload: "No files selected" 
      });
      return;
    }

    if (!eventId) {
      yield put({ type: commonConstants.showErrorMessage, payload: "Event ID is missing. Open this step from an existing event." });
      yield put({ 
        type: mediaConstants.uploadMediaFailure, 
        payload: "Event ID is missing" 
      });
      return;
    }

    if (!studioId) {
      yield put({ type: commonConstants.showErrorMessage, payload: "Studio ID is missing. Please refresh and try again." });
      yield put({ 
        type: mediaConstants.uploadMediaFailure, 
        payload: "Studio ID is missing" 
      });
      return;
    }

    // Create an event channel for progress updates.
    // Use an expanding buffer so rapid per-chunk events are never dropped.
    let progressEmitter: any;
    const progressChannel = eventChannel((emitter) => {
      progressEmitter = emitter;
      return () => {};
    }, buffers.expanding(100));

    // Fork a saga to watch for progress updates
    yield fork(watchProgress, progressChannel);

    // Seed every file at 0% immediately so the progress bar appears before
    // the first chunk callback fires (important for small/fast files).
    for (const file of files) {
      const fileName: string = (file.originFileObj as File | undefined)?.name ?? file.name;
      yield put({
        type: mediaConstants.uploadMediaProgress,
        payload: { fileName, percentage: 0 },
      });
    }

    // Call the upload service with progress callback
    const result = yield call(
      mediaService.uploadImages,
      files,
      studioId,
      eventId,
      serviceId,
      {
        onProgress: (progress: UploadImagesProgress) => {
          // Emit progress updates through the channel
          if (progressEmitter) {
            progressEmitter({
              type: mediaConstants.uploadMediaProgress,
              payload: {
                fileName: progress.fileName,
                percentage: progress.percentage,
              },
            });
          }
        },
      },
      mediaType
    );

    // Close the progress channel
    progressChannel.close();

    // Remove successfully uploaded files from the list one-by-one so parallel
    // image and video batches don't overwrite each other's file entries.
    for (const item of result.success) {
      const file = files.find(
        (f: any) => (f.originFileObj?.name || f.name) === item.fileName
      );
      if (file) {
        yield put({ type: mediaConstants.removeFileFromList, payload: file.uid });
      }
    }

    // Map success items to include size from original files
    const uploadedFilesWithDetails = result.success.map((item: any) => {
      const originalFile = files.find(
        (f: any) => (f.originFileObj?.name || f.name) === item.fileName
      );
      return {
        name: item.fileName,
        size: originalFile?.size || originalFile?.originFileObj?.size || 0,
        url: undefined,
        thumbUrl: originalFile?.thumbUrl || undefined,
      };
    });

    // Dispatch success (no remainingFiles — handled above via removeFileFromList)
    yield put({
      type: mediaConstants.uploadMediaSuccess,
      payload: {
        success: uploadedFilesWithDetails,
        failed: result.failed,
      },
    });

    // Show success/failure messages
    if (result.success.length) {
      yield put({
        type: commonConstants.showSuccessMessage,
        payload: `${result.success.length} ${mediaType === "videos" ? "video" : "image"}${result.success.length > 1 ? "s" : ""} uploaded successfully`,
      });
    }

    if (result.failed.length) {
      yield put({
        type: commonConstants.showErrorMessage,
        payload: `${result.failed.length} ${mediaType === "videos" ? "video" : "image"}${result.failed.length > 1 ? "s" : ""} failed to upload`,
      });
    }
  } catch (error: any) {
    console.error("Upload error:", error);
    yield put({ 
      type: mediaConstants.uploadMediaFailure, 
      payload: error?.message || "Failed to upload files" 
    });
    yield put({ type: commonConstants.showErrorMessage, payload: error?.message || "Failed to upload files" });
  }
}

function* watchProgress(channel: any): any {
  try {
    while (true) {
      const action = yield take(channel);
      yield put(action);
    }
  } finally {
    // Channel closed
  }
}

function* updateMediaStatus(action: any): any {
    try {
      const payload = action.payload || {};
      const { studioId, eventId, isSubmitted, isAcknowledged } = payload;

      if (!studioId || !eventId) {
        throw new Error("Studio ID and Event ID are required");
      }

      if (typeof isSubmitted !== "boolean" && typeof isAcknowledged !== "boolean") {
        throw new Error("No media status field provided");
      }

      const response: any = yield call(mediaService.acknowledgeMedia, payload);
      if (!response?.success) {
        throw new Error(response?.message || response?.errorMessage || "Failed to update media status");
      }

      yield put({
        type: mediaConstants.updateMediaStatusSuccess,
        payload: response?.resultObject || null,
      });

      yield put({ type: commonConstants.showSuccessMessage, payload: response?.message ||  "Media status updated successfully" });
    } catch (error: any) {
      yield put({
        type: mediaConstants.updateMediaStatusFailure,
        payload: error?.message || "Failed to update media status",
      });
      yield put({ type: commonConstants.showErrorMessage, payload: error?.message || "Failed to update media status" });
  }
}
function* listMedia(action: any): any {
    try {
      const payload = action.payload || {};
      const { studioId, eventId, type, limit = 20, append = false, serviceId = null } = payload;

      let items: any[] = [];
      let nextContinuationTokenImages: string | null = null;
      let nextContinuationTokenVideos: string | null = null;
      let hasMoreImages = false;
      let hasMoreVideos = false;

      if (!type || type === 'all') {
        // Fetch both types in parallel
        const [imgs, vids]: any[] = yield all([
          call(mediaService.listMedia, {
            studioId, eventId, serviceId, type: 'images', limit,
            continuationToken: payload.continuationTokenImages || null,
          }),
          call(mediaService.listMedia, {
            studioId, eventId, serviceId, type: 'videos', limit,
            continuationToken: payload.continuationTokenVideos || null,
          }),
        ]);
        if (imgs?.success) {
          items = items.concat(imgs.resultObject?.items || []);
          nextContinuationTokenImages = imgs.resultObject?.nextContinuationToken ?? null;
          hasMoreImages = imgs.resultObject?.hasMore ?? false;
        }
        if (vids?.success) {
          items = items.concat(vids.resultObject?.items || []);
          nextContinuationTokenVideos = vids.resultObject?.nextContinuationToken ?? null;
          hasMoreVideos = vids.resultObject?.hasMore ?? false;
        }
      } else {
        const resp: any = yield call(mediaService.listMedia, {
          studioId, eventId, serviceId, type, limit,
          continuationToken: payload.continuationToken || null,
        });
        if (resp?.success) {
          items = resp.resultObject?.items || [];
          if (type === 'images') {
            nextContinuationTokenImages = resp.resultObject?.nextContinuationToken ?? null;
            hasMoreImages = resp.resultObject?.hasMore ?? false;
          } else {
            nextContinuationTokenVideos = resp.resultObject?.nextContinuationToken ?? null;
            hasMoreVideos = resp.resultObject?.hasMore ?? false;
          }
        }
      }

      yield put({
        type: mediaConstants.listMediaSuccess,
        payload: {
          items,
          append,
          nextContinuationTokenImages,
          nextContinuationTokenVideos,
          hasMoreImages,
          hasMoreVideos,
        },
      });
    } catch (error: any) {
      yield put({ type: mediaConstants.listMediaFailure, payload: error?.message || 'Failed to fetch media' });
      yield put({ type: commonConstants.showErrorMessage, payload: error?.message || 'Failed to fetch media' });
    }
  }

export const mediaSaga = {
  uploadMedia,
  updateMediaStatus,
  listMedia
};
